import { useEffect, useRef } from "react";
import { connectZoho, ZOHO_REDIRECT_URI } from "../ZohoAuthHelper";

export default function ZohoCallback() {
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;

    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const errorParam = params.get("error");

    // `state` carries the tenant subdomain origin plus a `flow` tag, matching
    // GoogleCallback.js's convention.
    const stateParam = params.get("state");
    let openerOrigin = window.location.origin;

    if (stateParam) {
      const decoded = decodeURIComponent(stateParam);
      try {
        const parsed = JSON.parse(decoded);
        openerOrigin = parsed.origin || openerOrigin;
      } catch {
        openerOrigin = decoded;
      }
    }

    // Zoho's redirect_uri is a fixed, non-tenant host (Zoho requires an exact
    // registered match, so it can't vary per subdomain) — on a tenant subdomain this
    // popup lands on a DIFFERENT origin than the one that started the flow, with no
    // access to that origin's localStorage/JWT. The connect call is authenticated, so
    // bounce the popup back to the real tenant origin (carrying the same code+state)
    // before calling it. Mirrors GoogleCallback.js.
    if (window.location.origin !== openerOrigin) {
      window.location.replace(
        openerOrigin + window.location.pathname + window.location.search
      );
      return;
    }

    done.current = true;

    // window.close() right after postMessage() races the browser's dispatch of
    // that message to the opener — a short delay lets it flush before the popup
    // tears down. Mirrors GoogleCallback.js.
    const notifyOpenerAndClose = (message) => {
      if (window.opener) window.opener.postMessage(message, openerOrigin);
      setTimeout(() => window.close(), 250);
    };

    if (!code) {
      notifyOpenerAndClose({
        type: "ZOHO_OAUTH_ERROR",
        message: errorParam || "No authorization code received.",
      });
      return;
    }

    // Always exchange with the same fixed redirect_uri that was sent to the
    // /auth endpoint (ZOHO_REDIRECT_URI) — not the current (possibly
    // tenant-bounced) window location, since Zoho requires an exact match.
    connectZoho(code, ZOHO_REDIRECT_URI)
      .then(() => notifyOpenerAndClose({ type: "ZOHO_OAUTH_SUCCESS" }))
      .catch(() => notifyOpenerAndClose({ type: "ZOHO_OAUTH_ERROR" }));
  }, []);

  return <p>Connecting to Zoho…</p>;
}
